import { SuperEmojiEffect, Position, CapturedEmoji } from '../utils/types';
import { superEmojiService, SUPER_EMOJIS } from './superEmojiService';

export class SuperEmojiPowers {
  private static instance: SuperEmojiPowers;

  private constructor() {}

  static getInstance(): SuperEmojiPowers {
    if (!SuperEmojiPowers.instance) {
      SuperEmojiPowers.instance = new SuperEmojiPowers();
    }
    return SuperEmojiPowers.instance;
  }

  // Activate dance power (💃)
  activateDance(
    windowSize: { width: number; height: number },
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void
  ): SuperEmojiEffect {
    const config = superEmojiService.getSuperEmojiConfig('💃')!;
    const bouncePoints = superEmojiService.generateBouncePoints(windowSize, config.count!);
    
    const effect: SuperEmojiEffect = {
      id: `dance-${Date.now()}`,
      type: 'dance',
      emoji: '💃',
      startTime: performance.now(),
      duration: config.duration! * 1000,
      isActive: true,
      positions: bouncePoints,
      progress: 0
    };

    // Start bounce animation with collision detection
    this.animateDance(effect, ufos, onUFODestroy, config.radius!);
    
    return effect;
  }

  // Activate sword power (⚔️)
  activateSword(
    windowSize: { width: number; height: number },
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void
  ): SuperEmojiEffect {
    const config = superEmojiService.getSuperEmojiConfig('⚔️')!;
    const trajectories = superEmojiService.generateSwordTrajectories(windowSize);
    
    const effect: SuperEmojiEffect = {
      id: `sword-${Date.now()}`,
      type: 'sword',
      emoji: '⚔️',
      startTime: performance.now(),
      duration: config.duration! * 1000,
      isActive: true,
      positions: trajectories[0], // Start positions
      targetPositions: trajectories[1], // End positions
      progress: 0
    };

    // Start sword animation with collision detection
    this.animateSword(effect, ufos, onUFODestroy, config.radius!);
    
    return effect;
  }

  // Activate parachute power (🪂)
  activateParachute(
    windowSize: { width: number; height: number },
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void
  ): SuperEmojiEffect {
    const config = superEmojiService.getSuperEmojiConfig('🪂')!;
    const startPositions = superEmojiService.generateParachutePositions(windowSize, config.count!);
    
    const effect: SuperEmojiEffect = {
      id: `parachute-${Date.now()}`,
      type: 'parachute',
      emoji: '🪂',
      startTime: performance.now(),
      duration: config.duration! * 1000,
      isActive: true,
      positions: startPositions,
      progress: 0
    };

    // Start parachute animation with collision detection
    this.animateParachute(effect, windowSize, ufos, onUFODestroy, config.radius!);
    
    return effect;
  }

  // Activate bomb power (💣)
  activateBomb(
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void
  ): SuperEmojiEffect {
    const effect: SuperEmojiEffect = {
      id: `bomb-${Date.now()}`,
      type: 'bomb',
      emoji: '💣',
      startTime: performance.now(),
      duration: 1000, // 1 second for explosion effect
      isActive: true,
      progress: 0
    };

    // Destroy all current UFOs with staggered timing
    const activeUfos = ufos.filter(ufo => ufo.status === 'captured');
    activeUfos.forEach((ufo, index) => {
      setTimeout(() => onUFODestroy(ufo.id), index * 50); // 50ms between each destruction
    });

    console.log(`💣 Bomb activated! Destroying ${activeUfos.length} UFOs`);

    return effect;
  }

  // Activate fire heart power (❤️‍🔥)
  activateFireHeart(
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void,
    onLevelComplete: () => void
  ): SuperEmojiEffect {
    const effect: SuperEmojiEffect = {
      id: `fire-${Date.now()}`,
      type: 'fire',
      emoji: '❤️‍🔥',
      startTime: performance.now(),
      duration: 2000, // 2 seconds for complete effect
      isActive: true,
      progress: 0
    };

    // Destroy all current UFOs with staggered timing
    const activeUfos = ufos.filter(ufo => ufo.status === 'captured');
    activeUfos.forEach((ufo, index) => {
      setTimeout(() => onUFODestroy(ufo.id), index * 30); // 30ms between each destruction
    });

    // Complete level after effect
    setTimeout(() => {
      console.log('❤️‍🔥 Fire Heart completed level!');
      onLevelComplete();
    }, 1500);

    console.log(`❤️‍🔥 Fire Heart activated! Destroying ${activeUfos.length} UFOs and completing level`);

    return effect;
  }

  // Animation helpers
  private animateDance(
    effect: SuperEmojiEffect,
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void,
    radius: number
  ) {
    // Pre-calculate all collision times since UFOs are now frozen
    const collisions = this.calculateDanceCollisions(effect.positions!, ufos, radius, effect.duration);
    
    // Schedule all UFO destructions at their calculated collision times
    collisions.forEach(collision => {
      setTimeout(() => {
        onUFODestroy(collision.ufoId);
        console.log(`💃 Dance hit ${collision.ufoId} at bounce ${collision.bounceIndex + 1} (${collision.time}ms) - Smooth collision!`);
      }, collision.time);
    });
    
    console.log(`💃 Pre-calculated ${collisions.length} dance collisions for smooth animation`);
  }

  private animateSword(
    effect: SuperEmojiEffect,
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void,
    radius: number
  ) {
    const trajectories = superEmojiService.generateSwordTrajectories({
      width: window.innerWidth,
      height: window.innerHeight
    });
    
    // Pre-calculate all collision times since UFOs are now frozen
    const collisions = this.calculateSwordCollisions(trajectories, ufos, radius, effect.duration);
    
    // Schedule all UFO destructions at their calculated collision times
    collisions.forEach(collision => {
      setTimeout(() => {
        onUFODestroy(collision.ufoId);
        console.log(`⚔️ Sword ${collision.swordIndex + 1} hit ${collision.ufoId} at ${collision.time}ms - Smooth collision!`);
      }, collision.time);
    });
    
    console.log(`⚔️ Pre-calculated ${collisions.length} sword collisions for smooth animation`);
  }

  private animateParachute(
    effect: SuperEmojiEffect,
    windowSize: { width: number; height: number },
    ufos: CapturedEmoji[],
    onUFODestroy: (ufoId: string) => void,
    radius: number
  ) {
    // Pre-calculate all collision times since UFOs are now frozen
    const collisions = this.calculateParachuteCollisions(effect.positions!, ufos, radius, effect.duration, windowSize.height);
    
    // Schedule all UFO destructions at their calculated collision times
    collisions.forEach(collision => {
      setTimeout(() => {
        onUFODestroy(collision.ufoId);
        console.log(`🪂 Parachute ${collision.parachuteIndex + 1} hit ${collision.ufoId} at ${collision.time}ms - Smooth collision!`);
      }, collision.time);
    });
    
    console.log(`🪂 Pre-calculated ${collisions.length} parachute collisions for smooth animation`);
  }

  // Collision calculation methods for smooth animations
  private calculateDanceCollisions(
    bouncePoints: Position[],
    ufos: CapturedEmoji[],
    radius: number,
    duration: number
  ): Array<{ ufoId: string; bounceIndex: number; time: number }> {
    const collisions: Array<{ ufoId: string; bounceIndex: number; time: number }> = [];
    const activeUfos = ufos.filter(ufo => ufo.status === 'captured');
    
    // Calculate timing for each bounce point
    // Animation keyframes: 0%, 20%, 25%, 40%, 50%, 65%, 75%, 90%, 100%
    // Actual bounce points (when dancer lands): 0%, 25%, 50%, 75%, 100%
    const bounceTimings = [0, 0.25, 0.5, 0.75, 1.0]; // When dancer actually touches each point
    
    bouncePoints.forEach((point, bounceIndex) => {
      if (bounceIndex < bounceTimings.length) {
        const bounceTime = bounceTimings[bounceIndex] * duration;
        
        activeUfos.forEach(ufo => {
          // Check if UFO is within collision radius of this bounce point
          const distance = Math.sqrt(
            (ufo.position.x - point.x) ** 2 + (ufo.position.y - point.y) ** 2
          );
          
          if (distance <= radius) {
            collisions.push({
              ufoId: ufo.id,
              bounceIndex,
              time: Math.round(bounceTime)
            });
          }
        });
      }
    });
    
    // Sort by time to ensure proper destruction order
    return collisions.sort((a, b) => a.time - b.time);
  }

  private calculateSwordCollisions(
    trajectories: Position[][],
    ufos: CapturedEmoji[],
    radius: number,
    duration: number
  ): Array<{ ufoId: string; swordIndex: number; time: number }> {
    const collisions: Array<{ ufoId: string; swordIndex: number; time: number }> = [];
    const activeUfos = ufos.filter(ufo => ufo.status === 'captured');
    
    trajectories.forEach((trajectory, swordIndex) => {
      const start = trajectory[0];
      const end = trajectory[1];
      
      activeUfos.forEach(ufo => {
        // Calculate when sword path will intersect with UFO position
        const intersectionTime = this.calculateLineToPointIntersectionTime(
          start, end, ufo.position, radius, duration
        );
        
        if (intersectionTime !== null && intersectionTime >= 0 && intersectionTime <= duration) {
          collisions.push({
            ufoId: ufo.id,
            swordIndex,
            time: Math.round(intersectionTime)
          });
        }
      });
    });
    
    // Sort by time to ensure proper destruction order
    return collisions.sort((a, b) => a.time - b.time);
  }

  private calculateParachuteCollisions(
    parachutePositions: Position[],
    ufos: CapturedEmoji[],
    radius: number,
    duration: number,
    screenHeight: number
  ): Array<{ ufoId: string; parachuteIndex: number; time: number }> {
    const collisions: Array<{ ufoId: string; parachuteIndex: number; time: number }> = [];
    const activeUfos = ufos.filter(ufo => ufo.status === 'captured');
    
    parachutePositions.forEach((startPos, parachuteIndex) => {
      const fallDistance = screenHeight + 100; // Total fall distance
      
      activeUfos.forEach(ufo => {
        // Check if UFO is within horizontal range of parachute path
        const horizontalDistance = Math.abs(ufo.position.x - startPos.x);
        if (horizontalDistance <= radius) {
          // Calculate when parachute will reach UFO's Y position
          const ufoY = ufo.position.y;
          const startY = startPos.y;
          
          if (ufoY >= startY && ufoY <= (startY + fallDistance)) {
            // Calculate time when parachute reaches UFO's Y position
            const fallProgress = (ufoY - startY) / fallDistance;
            const intersectionTime = fallProgress * duration;
            
            if (intersectionTime >= 0 && intersectionTime <= duration) {
              collisions.push({
                ufoId: ufo.id,
                parachuteIndex,
                time: Math.round(intersectionTime)
              });
            }
          }
        }
      });
    });
    
    // Sort by time to ensure proper destruction order
    return collisions.sort((a, b) => a.time - b.time);
  }

  private calculateLineToPointIntersectionTime(
    lineStart: Position,
    lineEnd: Position,
    point: Position,
    radius: number,
    duration: number
  ): number | null {
    // Calculate the closest point on the line to the UFO position
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    const lineLength = Math.sqrt(dx * dx + dy * dy);
    
    if (lineLength === 0) return null;
    
    // Normalize the line direction
    const lineUnitX = dx / lineLength;
    const lineUnitY = dy / lineLength;
    
    // Vector from line start to point
    const pointDx = point.x - lineStart.x;
    const pointDy = point.y - lineStart.y;
    
    // Project point onto line to find closest point
    const projectedDistance = pointDx * lineUnitX + pointDy * lineUnitY;
    
    // Clamp to line segment
    const clampedDistance = Math.max(0, Math.min(lineLength, projectedDistance));
    
    // Closest point on line
    const closestX = lineStart.x + lineUnitX * clampedDistance;
    const closestY = lineStart.y + lineUnitY * clampedDistance;
    
    // Distance from UFO to closest point on line
    const distanceToLine = Math.sqrt(
      (point.x - closestX) ** 2 + (point.y - closestY) ** 2
    );
    
    // Check if within collision radius
    if (distanceToLine <= radius) {
      // Calculate time based on progress along the line
      const progress = clampedDistance / lineLength;
      return progress * duration;
    }
    
    return null;
  }
}

export const superEmojiPowers = SuperEmojiPowers.getInstance();